/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useState, useEffect } from "react";
import { listWatchlistItems, createWatchlistItem, deleteWatchlistItem } from "../api/api"; // Updated API imports

const WatchlistModal = ({ setWatchlistModalOpen, watchlist, setWatchlist }) => {
  const [newInstrument, setNewInstrument] = useState("");
  const [newExchange, setNewExchange] = useState(""); // Added state for exchange
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch watchlist from API on mount
  const fetchItems = async () => {
    setIsLoading(true);
    setError(null);
    console.log("WatchlistModal: Starting fetchItems...");
    try {
      const response = await listWatchlistItems();
      console.log("WatchlistModal: listWatchlistItems API returned:", response); 

      // Handle paginated response format {count, next, previous, results}
      const items = response?.results || response;
      const itemsArray = Array.isArray(items) ? items : [];
      
      console.log("WatchlistModal: Preparing to call Dashboard's setWatchlist with (length " + itemsArray.length + "):", JSON.parse(JSON.stringify(itemsArray)));
      setWatchlist(itemsArray); 
    } catch (err) {
      console.error("WatchlistModal: Error in fetchItems:", err); 
      setError(err.detail || "Failed to fetch watchlist items. Please try again.");
      console.log("WatchlistModal: Error caught in fetchItems. Calling Dashboard's setWatchlist with [].");
      setWatchlist([]); 
    } finally {
      setIsLoading(false);
      console.log("WatchlistModal: Finished fetchItems.");
    }
  };

  useEffect(() => {
    fetchItems();
  }, []); // Removed setWatchlist from dependency array as fetchItems handles it

  // Function to add an instrument
  const handleAddInstrument = async () => {
    if (newInstrument && newExchange) {
      setIsLoading(true);
      setError(null);
      try {
        // Check for duplicates locally before API call (optional, API handles it too)
        const exists = watchlist.some(
          item => item.instrument === newInstrument && item.exchange === newExchange
        );
        if (exists) {
          setError("This instrument/exchange combination already exists in your watchlist.");
          setIsLoading(false);
          return;
        }

        await createWatchlistItem({ instrument: newInstrument, exchange: newExchange });
        setNewInstrument("");
        setNewExchange("");
        await fetchItems(); // Refresh list
      } catch (err) {
        console.error("Failed to add watchlist item:", err);
        setError(err.detail || "Failed to add item. Check if it already exists or try again.");
      } finally {
        setIsLoading(false);
      }
    } else {
      setError("Both instrument and exchange are required.");
    }
  };

  // Function to remove an instrument
  const handleRemoveInstrument = async (itemId) => {
    setIsLoading(true);
    setError(null);
    try {
      await deleteWatchlistItem(itemId);
      await fetchItems(); // Refresh list
    } catch (err) {
      console.error("Failed to remove watchlist item:", err);
      setError(err.detail || "Failed to remove item. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md mx-4">
        <h3 className="text-xl font-semibold text-[var(--primary-color)] mb-4">Manage Watchlist</h3>

        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
        {isLoading && <p className="text-blue-500 text-sm mb-3">Loading...</p>}

        {/* Watchlist Items */}
        <div className="max-h-60 overflow-y-auto mb-4">
          <ul>
            {watchlist && watchlist.length > 0 ? (
              watchlist.map((item) => (
                <li key={item.id} className="flex justify-between items-center bg-[var(--secondary-color)] dark:bg-gray-700 p-3 rounded-lg mb-2 shadow">
                  <span className="text-[var(--text-color)] dark:text-gray-200">
                    {item.instrument} ({item.exchange})
                    {item.is_global && <span className="text-xs text-blue-500 ml-2">(Global)</span>}
                  </span>
                  <button
                    className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-600 text-xl"
                    onClick={() => handleRemoveInstrument(item.id)}
                    disabled={isLoading}
                  >
                    &times;
                  </button>
                </li>
              ))
            ) : (
              !isLoading && <p className="text-[var(--text-color)] dark:text-gray-400 text-center py-4">No instruments in watchlist.</p>
            )}
          </ul>
        </div>

        {/* Add Instrument Inputs */}
        <div className="space-y-3">
          <input
            type="text"
            className="w-full p-2 border rounded-lg bg-[var(--secondary-color)] dark:bg-gray-700 text-[var(--text-color)] dark:text-gray-200 dark:border-gray-600"
            placeholder="Instrument (e.g., AAPL, EURUSD)"
            value={newInstrument}
            onChange={(e) => setNewInstrument(e.target.value)}
            disabled={isLoading}
          />
          <input
            type="text"
            className="w-full p-2 border rounded-lg bg-[var(--secondary-color)] dark:bg-gray-700 text-[var(--text-color)] dark:text-gray-200 dark:border-gray-600"
            placeholder="Exchange (e.g., NASDAQ, FXCM)"
            value={newExchange}
            onChange={(e) => setNewExchange(e.target.value)}
            disabled={isLoading}
          />
          <button
            className="btn btn-primary w-full"
            onClick={handleAddInstrument}
            disabled={isLoading || !newInstrument || !newExchange}
          >
            {isLoading ? "Adding..." : "Add to Watchlist"}
          </button>
        </div>

        {/* Close Button */}
        <button
          className="btn btn-secondary mt-3 w-full"
          onClick={() => setWatchlistModalOpen(false)}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default WatchlistModal;
